import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';
import {
  FRIEND_CARD_CREST_OFFSET_RATIO,
  FRIEND_CARD_CREST_SIZE_RATIO,
} from '../../../src/economy/constants/playerRanks.constants';
import type { getCardDimensions } from './FriendCardBase.styles';

export const getFriendCardAvatarStyles = (theme: Theme, themeMode?: ThemeMode) =>
  StyleSheet.create({
    avatarContainer: {
      position: 'relative',
      marginBottom: 6,
    },
    // Podium (approved design): the avatar "stands" on a thin metallic bar over a soft shadow pool.
    podiumShadowPool: {
      position: 'absolute',
      bottom: -2,
      left: '4%',
      right: '4%',
      height: 14,
      borderRadius: 9999,
      backgroundColor: themeMode === 'dark' ? 'rgba(0, 0, 0, 0.45)' : 'rgba(0, 0, 0, 0.12)',
      zIndex: 0,
    },
    podiumFloorBar: {
      position: 'absolute',
      bottom: 5,
      left: '10%',
      right: '10%',
      height: 6,
      borderRadius: 9999,
      backgroundColor: themeMode === 'dark' ? theme.gray[400] : theme.gray[300],
      zIndex: 1,
    },
    avatarOnStage: {
      zIndex: 2,
      transform: [{ scale: themeMode === 'dark' ? 1.02 : 1.05 }],
      ...(themeMode === 'dark'
        ? {
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.2,
            shadowRadius: 14,
            elevation: 4,
          }
        : {
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.2,
            shadowRadius: 6,
            elevation: 6,
          }),
    },
    avatarImage: {
      width: 90,
      height: 90,
      borderRadius: 45,
      backgroundColor: theme.gray[200],
    },
    avatarImageGroupBg: {
      backgroundColor: theme.gray[200],
    },
    avatar: {
      fontSize: 32,
    },
    statusDot: {
      position: 'absolute',
      bottom: 4,
      right: 4,
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 3,
      borderColor: theme.background.card,
    },
    // Corner is set by profileBadgeCornerRight/Left — the chip yields the bottom-right to the
    // rank crest, but ONLY when a crest is actually there (specs/rank-on-social-screens AC-7).
    profileBadge: {
      position: 'absolute',
      bottom: 0,
      zIndex: 3,
      elevation: 8,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2.5,
      borderColor: theme.background.card,
      overflow: 'hidden',
    },
    /** Default corner — where the chip has always lived. */
    profileBadgeCornerRight: {
      right: 0,
    },
    /**
     * Only when a rank crest occupies the bottom-right (AC-7). Without a crest the chip must
     * stay right, or the loading / failure / old-server states would shift it for nothing —
     * those states must look exactly like today (AC-14).
     */
    profileBadgeCornerLeft: {
      left: 0,
    },
    profileBadgeImage: {
      width: '100%',
      height: '100%',
    },
    profileBadgeInitials: {
      backgroundColor: theme.primary + '30',
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileBadgeInitialsText: {
      color: theme.primary,
      fontWeight: '700',
    },
  });

export const getAvatarContainerStyle = (dimensions: ReturnType<typeof getCardDimensions>) => ({
  marginBottom: dimensions.avatarMarginBottom,
});

export const getAvatarImageStyle = (dimensions: ReturnType<typeof getCardDimensions>) => ({
  width: dimensions.avatarSize,
  height: dimensions.avatarSize,
  borderRadius: dimensions.avatarSize / 2,
});

export const getGroupImageStyle = (dimensions: ReturnType<typeof getCardDimensions>) => ({
  width: dimensions.groupImageSize,
  height: dimensions.groupImageSize,
  borderRadius: Math.max(12, dimensions.groupImageSize * 0.12),
});

export const getStatusDotStyle = (
  theme: Theme,
  dimensions: ReturnType<typeof getCardDimensions>,
  isOnline: boolean
) => ({
  width: dimensions.statusDotSize,
  height: dimensions.statusDotSize,
  borderRadius: dimensions.statusDotSize / 2,
  borderWidth: dimensions.statusDotBorder,
  borderColor: theme.background.card,
  backgroundColor: isOnline ? theme.success : theme.gray[400],
});

/**
 * Rank crest + level pill on the friend card (specs/rank-on-social-screens AC-7, design 1b).
 * Ratios, not fixed px: `friendAvatarSize` scales with `cardWidth` across the responsive grid.
 * Both overhang the avatar so the card never grows to fit them (spec §7).
 */
export const getRankCrestStyle = (dimensions: ReturnType<typeof getCardDimensions>) => {
  const size = dimensions.friendAvatarSize * FRIEND_CARD_CREST_SIZE_RATIO;
  const offset = dimensions.friendAvatarSize * FRIEND_CARD_CREST_OFFSET_RATIO;
  return {
    size,
    style: {
      position: 'absolute',
      right: -offset,
      bottom: -offset,
      zIndex: 4,
    } as const,
  };
};

/** Centered on the avatar's bottom edge, above the crest so a wide "LV 100" still reads. */
export const getRankLevelPillStyle = () =>
  ({
    position: 'absolute',
    bottom: -6,
    alignSelf: 'center',
    zIndex: 5,
  }) as const;

/** Styles for profile badge (profile pic or initials) at bottom-LEFT of avatar. */
export const getProfileBadgeStyle = (
  theme: Theme,
  dimensions: ReturnType<typeof getCardDimensions>,
  themeMode?: ThemeMode
) => {
  const isDark = themeMode === 'dark'
  return {
    container: {
      width: dimensions.profileBadgeSize,
      height: dimensions.profileBadgeSize,
      borderRadius: dimensions.profileBadgeSize / 2,
      borderWidth: isDark ? 1 : dimensions.profileBadgeBorder,
      borderColor: isDark ? theme.gray[500] : theme.background.card,
    } as const,
    initialsText: {
      fontSize: dimensions.profileBadgeSize * 0.4,
    } as const,
  }
}
